import { z } from "zod";
import { UuidSchema, TimestampsSchema, DateTimeSchema } from "./common.js";
import { IconNameSchema } from "./icon.js";
import { EntrypointUrlSchema, ImportSourceSchema, ImportCampaignDataSchema, } from "./vast.js";
import { EndpointFiltersSchema } from "./exchange.js";
import { findUnknownMacros } from "./pop.js";
// Stored as an integer on the DB (mirrors SegmentType). Values are stable —
// appending a new variant is safe (use the next integer); renumbering breaks
// existing rows.
export var CampaignType;
(function (CampaignType) {
    CampaignType[CampaignType["Standard"] = 0] = "Standard";
    CampaignType[CampaignType["Transit"] = 1] = "Transit";
    CampaignType[CampaignType["Popunder"] = 2] = "Popunder";
})(CampaignType || (CampaignType = {}));
export const CAMPAIGN_TYPE_LABELS = {
    [CampaignType.Standard]: "Standard",
    [CampaignType.Transit]: "Transit",
    [CampaignType.Popunder]: "Popunder",
};
export const CampaignTypeSchema = z.nativeEnum(CampaignType);
// A transit (RTB) campaign's config; persisted inside the campaign's `metadata`.
export const TransitCampaignMetadataSchema = z.object({
    entrypoint: EntrypointUrlSchema,
    // Our commission, as a percent of the SSP floor, taken before the 50/50
    // profit split when the SSP is on profit_share pricing. Absent ⇒ 0.
    margin: z.number().int().min(0).optional(),
    // Max OUTBOUND requests/second sent to this DSP endpoint (cluster-wide).
    qps: z.number().int().positive(),
    // Per-DSP response timeout (ms). Effective deadline = min(timeoutMs, the
    // SSP's tmax minus buffer). Absent ⇒ TRANSIT_DEFAULT_TMAX_MS.
    timeoutMs: z.number().int().min(20).max(5000).optional(),
    // Routing filters applied to each request BEFORE it is sent to this DSP.
    filters: EndpointFiltersSchema.nullable().optional(),
    // Read-only public statistics token for this DSP endpoint (server-minted).
    statsToken: z.string().optional(),
    selectedPublishers: z.array(UuidSchema),
    // Advertiser-domain block list: filters the DSP's `bid.adomain` in the
    // response and is forwarded upstream as `badv`.
    blockList: z.array(z.string()),
    // Publisher/site-domain allow list: when non-empty, the campaign only bids on
    // incoming requests whose `site.domain` (or `app.domain`) matches an entry.
    // Empty or absent ⇒ no site restriction (bid on all). Optional so transit
    // rows persisted before this field existed still validate.
    whiteList: z.array(z.string()).optional(),
});
// --- Popunder ----------------------------------------------------------------
//
// A popunder campaign buys pop traffic over RTB and redirects the user to an
// advertiser/tracker destination. Its whole config lives in `metadata`.
// Two shapes per object below: an INPUT shape with no defaults, used inside the
// campaign metadata union that becomes the HTTP JSON schema (Ajv runs in strict
// mode and refuses `default` inside a union branch, and its removeAdditional
// would mutate data while probing branches), and a NORMALISED shape with
// defaults that the server applies after the request validates
// (`normalizePopunderMetadata`). Stored rows are always normalised.
// LEGACY, read-only: country / region targeting stored on pop campaigns before
// audience filters moved to segments (2026-10-13). New saves never write it and
// the API refuses it; the pop index does not serve a campaign that still has
// entries here (it must be converted to a segment first), so a deploy can never
// widen a campaign's targeting. Kept parseable so postbacks for such a campaign
// still resolve until it is converted.
export const LegacyPopGeoSchema = z
    .object({
    include: z.array(z.string()).optional(),
    exclude: z.array(z.string()).optional(),
    excludeRegions: z.array(z.string()).optional(),
})
    .strict();
export const legacyPopGeoHasEntries = (geo) => Boolean(geo &&
    ((geo.include?.length ?? 0) > 0 ||
        (geo.exclude?.length ?? 0) > 0 ||
        (geo.excludeRegions?.length ?? 0) > 0));
// Per-user frequency cap: `count` admitted visits per fixed `windowSeconds`.
const popCapFields = {
    count: z.number().int().min(1).max(10),
    windowSeconds: z.number().int().min(3600).max(604800),
};
export const PopCapInputSchema = z
    .object({
    count: popCapFields.count.optional(),
    windowSeconds: popCapFields.windowSeconds.optional(),
})
    .strict();
export const PopCapSchema = z
    .object({
    count: popCapFields.count.default(1),
    windowSeconds: popCapFields.windowSeconds.default(86400),
})
    .strict();
// Destination template: absolute http(s) URL, printable ASCII without
// whitespace, quotes or backslashes, ≤ 2000 chars, whitelisted `{macro}`s only.
// The macro check is a refinement, so it runs in the server's zod parse, not in
// the HTTP JSON schema.
export const POP_DESTINATION_MAX = 2000;
const PopDestinationShapeSchema = z
    .string()
    .min(1)
    .max(POP_DESTINATION_MAX)
    .regex(/^https?:\/\/[\x21\x23-\x5b\x5d-\x7e]+$/, {
    message: "destination must be an http(s) URL of printable ASCII without spaces, quotes or backslashes",
});
export const PopDestinationSchema = PopDestinationShapeSchema.superRefine((value, ctx) => {
    const unknown = findUnknownMacros(value);
    if (unknown.length > 0) {
        ctx.addIssue({
            code: "custom",
            message: `unknown macro(s): ${unknown.map((m) => `{${m}}`).join(", ")}`,
        });
    }
});
// SSP zone / placement id and site domain, as matched by the placement lists.
export const PopZoneIdSchema = z.string().regex(/^[A-Za-z0-9._:-]{1,64}$/);
export const PopSiteDomainSchema = z
    .string()
    .regex(/^[a-z0-9.-]{1,253}$/, { message: "lower-case domain expected" });
// Set only by the Phase 2 landing-check job; a client-sent value is ignored
// (the route keeps the stored one).
export const PopSafetyBlockSchema = z
    .object({ reason: z.string().max(200), at: z.string() })
    .strict();
const popFields = {
    selectedPublishers: z.array(UuidSchema).min(1),
    // Our margin as a percent of the sell CPM: bid = cpm × (1 − margin).
    marginPct: z.number().int().min(1).max(90),
    // Placement whitelists: when non-empty, the campaign bids ONLY on requests
    // whose zone / site domain (or a parent domain) is listed; a request without
    // one never matches. Empty ⇒ no restriction.
    includedZones: z.array(PopZoneIdSchema).max(500),
    includedSiteDomains: z.array(PopSiteDomainSchema).max(500),
    excludedZones: z.array(PopZoneIdSchema).max(500),
    excludedSiteDomains: z.array(PopSiteDomainSchema).max(500),
    // `{cost}` macro basis: what the advertiser pays (sell) or what we pay (buy).
    costMacro: z.enum(["sell", "buy"]),
    // How a visit becomes billable: a tracker `landing` postback, or a redirect
    // whose fingerprint matches the bid. Both modes are always available.
    confirmMode: z.enum(["tracker", "fingerprint"]),
    // Conversion status the performance phase optimises on; all are recorded.
    cpaTarget: z.enum(["fdp", "registration"]),
    // Server-minted secret for the tracker postback URL; preserved across saves.
    postbackToken: z.string().max(128).optional(),
    // Optional advertiser domain reported in the bid's `adomain`.
    adomain: PopSiteDomainSchema.optional(),
    safetyBlock: PopSafetyBlockSchema.optional(),
};
// INPUT shape (HTTP union member): no defaults, no refinements.
export const PopunderCampaignMetadataInputSchema = z.object({
    // Read-side only: lets API responses carry an unconverted row's legacy geo.
    // The server refuses it on writes (preparePopunderMetadata).
    geo: LegacyPopGeoSchema.optional(),
    destination: PopDestinationShapeSchema,
    selectedPublishers: popFields.selectedPublishers,
    cap: PopCapInputSchema.optional(),
    marginPct: popFields.marginPct.optional(),
    postbackToken: popFields.postbackToken,
    includedZones: popFields.includedZones.optional(),
    includedSiteDomains: popFields.includedSiteDomains.optional(),
    excludedZones: popFields.excludedZones.optional(),
    excludedSiteDomains: popFields.excludedSiteDomains.optional(),
    adomain: popFields.adomain,
    costMacro: popFields.costMacro.optional(),
    confirmMode: popFields.confirmMode.optional(),
    cpaTarget: popFields.cpaTarget.optional(),
    safetyBlock: popFields.safetyBlock,
});
// NORMALISED shape: what is stored and what the pop lane reads.
export const PopunderCampaignMetadataSchema = z
    .object({
    destination: PopDestinationSchema,
    selectedPublishers: popFields.selectedPublishers,
    geo: LegacyPopGeoSchema.optional(),
    cap: PopCapSchema.default({ count: 1, windowSeconds: 86400 }),
    marginPct: popFields.marginPct.default(15),
    postbackToken: popFields.postbackToken,
    includedZones: popFields.includedZones.default([]),
    includedSiteDomains: popFields.includedSiteDomains.default([]),
    excludedZones: popFields.excludedZones.default([]),
    excludedSiteDomains: popFields.excludedSiteDomains.default([]),
    adomain: popFields.adomain,
    costMacro: popFields.costMacro.default("sell"),
    confirmMode: popFields.confirmMode.default("fingerprint"),
    cpaTarget: popFields.cpaTarget.default("fdp"),
    safetyBlock: popFields.safetyBlock,
})
    .strict();
// Validate (incl. the macro whitelist) and fill defaults. Throws a ZodError.
export const normalizePopunderMetadata = (input) => PopunderCampaignMetadataSchema.parse(input);
// Fields are declared optional explicitly (not `.partial().extend()`, which
// does not compose under Zod 4) so a non-imported campaign may carry only
// `selectedPublishers` while imported campaigns keep full provenance. Empty/
// absent `selectedPublishers` ⇒ eligible for ALL providers. Kept field-
// compatible with CampaignImportMetadataSchema.
export const StandardCampaignMetadataSchema = z.object({
    source: ImportSourceSchema.optional(),
    url: z.string().optional(),
    importedAt: z.string().optional(),
    parsed: ImportCampaignDataSchema.optional(),
    raw: z.string().optional(),
    impressionDomain: z.string().nullable().optional(),
    selectedPublishers: z.array(UuidSchema).optional(),
});
// The `metadata` jsonb column is a closed set of known shapes: a transit
// campaign's config, a popunder campaign's config, or a standard campaign's
// import provenance + provider allow-list. `.strict()` on every branch keeps union discrimination
// order-insensitive — a malformed transit object cannot silently degrade to
// "standard", and a standard object cannot masquerade as transit. Kept 1-1
// with the DB column so persisted rows, API responses, and the frontend agree.
export const CampaignMetadataSchema = z.union([
    TransitCampaignMetadataSchema.strict(),
    PopunderCampaignMetadataInputSchema.strict(),
    StandardCampaignMetadataSchema.strict(),
]);
// Shape guards over the closed union (each branch has a field the others lack).
export const isTransitMetadata = (m) => m != null && typeof m === "object" && "entrypoint" in m;
export const isPopunderMetadata = (m) => m != null && typeof m === "object" && "destination" in m;
export const validateCampaignState = (state) => {
    const issues = [];
    const segments = state.selectedSegments ?? [];
    if (state.type === CampaignType.Transit) {
        if (!isTransitMetadata(state.metadata)) {
            issues.push({
                path: "metadata",
                message: "transit campaign requires transit metadata",
            });
        }
        if (segments.length > 0) {
            issues.push({
                path: "selectedSegments",
                message: "transit campaign cannot use segments",
            });
        }
    }
    else if (state.type === CampaignType.Popunder) {
        if (!isPopunderMetadata(state.metadata)) {
            issues.push({
                path: "metadata",
                message: "popunder campaign requires popunder metadata",
            });
        }
        if (state.defaultCreativeId != null) {
            issues.push({
                path: "defaultCreativeId",
                message: "popunder campaign cannot have creatives",
            });
        }
        if (state.cpm !== undefined && !(Number(state.cpm) > 0)) {
            issues.push({
                path: "cpm",
                message: "popunder campaign requires cpm > 0",
            });
        }
    }
    else {
        if (isTransitMetadata(state.metadata)) {
            issues.push({
                path: "metadata",
                message: "standard campaign cannot have transit metadata",
            });
        }
        if (isPopunderMetadata(state.metadata)) {
            issues.push({
                path: "metadata",
                message: "standard campaign cannot have popunder metadata",
            });
        }
    }
    return issues;
};
const refineCampaignState = (data, ctx) => {
    // An update that omits `type` keeps the stored type; the route validates the
    // merged state there (Zod cannot see the stored row).
    if (data.type === undefined)
        return;
    for (const issue of validateCampaignState({
        type: data.type,
        metadata: data.metadata,
        selectedSegments: data.selectedSegments,
        defaultCreativeId: data.defaultCreativeId,
        cpm: data.cpm,
    })) {
        ctx.addIssue({
            code: "custom",
            message: issue.message,
            path: [issue.path],
        });
    }
};
export const CampaignStatusSchema = z.enum([
    "draft",
    "pending",
    "approved",
    "active",
    "paused",
    "completed",
]);
// Per-creative re-show cooldown for a campaign: creativeUuid ⇒ seconds. Campaign
// data (a creative is shared; its cooldown belongs to the campaign that uses it).
export const CreativeCooldownsMapSchema = z.record(UuidSchema, z.number().int().positive());
export const LinkedCreativeSchema = z.object({
    uuid: UuidSchema,
    name: z.string().nullable(),
    url: z.string(),
    width: z.number().nullable(),
    height: z.number().nullable(),
    durationSeconds: z.number().nullable(),
});
export const CampaignBaseSchema = z
    .object({
    uuid: UuidSchema,
    name: z.string(),
    description: z.string().nullable(),
    status: z.string(),
    type: CampaignTypeSchema,
    selectedSegments: z.array(UuidSchema),
    userId: UuidSchema.nullable(),
    frequency: z.number(),
    defaultCreativeId: UuidSchema.nullable(),
    startDate: DateTimeSchema.nullable(),
    endDate: DateTimeSchema.nullable(),
    icon: IconNameSchema.nullable(),
    cpm: z.number(),
    dailyFrequency: z.number().int().nullable(),
    weeklyFrequency: z.number().int().nullable(),
    impressionsCap: z.number().int().nullable(),
    budgetCap: z.number().nullable(),
    cooldownSeconds: z.number().int().nullable().optional(),
    creativeCooldowns: CreativeCooldownsMapSchema.nullable().optional(),
    impressionDomain: z.string().nullable().optional(),
    creatives: z.array(LinkedCreativeSchema).optional(),
    metadata: CampaignMetadataSchema.nullable(),
})
    .merge(TimestampsSchema);
export const SegmentCreativesMapSchema = z.record(UuidSchema, UuidSchema);
export const CreateCampaignSchema = z
    .object({
    name: z.string().min(1),
    description: z.string().optional(),
    selectedSegments: z.array(UuidSchema),
    frequency: z.number().int().positive().optional().default(1),
    defaultCreativeId: UuidSchema.nullable().optional(),
    startDate: z.string().datetime().optional(),
    endDate: z.string().datetime().optional(),
    icon: IconNameSchema.nullable().optional(),
    cpm: z.number().min(0).optional().default(0),
    dailyFrequency: z.number().int().positive().nullable().optional(),
    weeklyFrequency: z.number().int().positive().nullable().optional(),
    impressionsCap: z.number().int().positive().nullable().optional(),
    budgetCap: z.number().min(0).nullable().optional(),
    cooldownSeconds: z.number().int().positive().nullable().optional(),
    creativeCooldowns: CreativeCooldownsMapSchema.nullable().optional(),
    segmentCreatives: SegmentCreativesMapSchema.optional(),
    type: CampaignTypeSchema.optional().default(CampaignType.Standard),
    metadata: CampaignMetadataSchema.optional(),
})
    // Publisher eligibility (transit / pop) is validated server-side (Zod can't
    // see the DB).
    .superRefine(refineCampaignState);
// Admin hands a campaign to another user (e.g. an agency account). Money and
// stats key on the campaign uuid, so only the owner changes.
export const TransferCampaignSchema = z.object({ userUuid: UuidSchema });
export const UpdateCampaignSchema = z
    .object({
    name: z.string().min(1).optional(),
    description: z.string().optional(),
    selectedSegments: z.array(UuidSchema).optional(),
    frequency: z.number().int().positive().optional(),
    defaultCreativeId: UuidSchema.nullable().optional(),
    startDate: z.string().datetime().optional(),
    endDate: z.string().datetime().optional(),
    icon: IconNameSchema.nullable().optional(),
    cpm: z.number().min(0).optional(),
    dailyFrequency: z.number().int().positive().nullable().optional(),
    weeklyFrequency: z.number().int().positive().nullable().optional(),
    impressionsCap: z.number().int().positive().nullable().optional(),
    budgetCap: z.number().min(0).nullable().optional(),
    cooldownSeconds: z.number().int().positive().nullable().optional(),
    creativeCooldowns: CreativeCooldownsMapSchema.nullable().optional(),
    segmentCreatives: SegmentCreativesMapSchema.optional(),
    // No default: an omitted type means "keep the stored type". (A default of
    // Standard made partial updates of transit/popunder campaigns look like a
    // type change.) The route rejects an explicit change of type.
    type: CampaignTypeSchema.optional(),
    metadata: CampaignMetadataSchema.optional(),
})
    .superRefine(refineCampaignState);
export const SetCampaignCreativesSchema = z.object({
    segmentCreatives: SegmentCreativesMapSchema,
});
export const CampaignResponseSchema = CampaignBaseSchema;
export const CampaignWithReceiversSchema = CampaignBaseSchema.extend({
    totalReceivers: z.number(),
    uniqueReceivers: z.number(),
});
// Pagination envelope used by paged list endpoints. (PaginationQuerySchema in
// common.ts is the inbound query shape; this one is the outbound response.)
export const PaginationEnvelopeSchema = z.object({
    limit: z.number(),
    offset: z.number(),
    total: z.number().optional(),
    hasMore: z.boolean(),
});
export const CampaignListResponseSchema = z.object({
    data: z.array(CampaignWithReceiversSchema),
    pagination: PaginationEnvelopeSchema,
});
export const CampaignAnalyticsSchema = z.object({
    campaignId: UuidSchema,
    days: z.number(),
    totalServed: z.number(),
    uniqueDevices: z.number(),
    dailyBreakdown: z.array(z.object({
        date: z.string(),
        served: z.number(),
        uniqueDevices: z.number(),
    })),
    segmentBreakdown: z.array(z.object({
        segmentId: UuidSchema,
        served: z.number(),
        uniqueDevices: z.number(),
    })),
});
export const CampaignCostSchema = z.object({
    estimatedCost: z.number().nullable(),
    realCost: z.number().nullable(),
});
// Lazy-loaded per-campaign all-time receivers stats. Cached on the backend
// in a 10-minute LRU. `spent` is Σ adServed.buyPrice / 1000 (the income
// board's buy-price basis, not cpm × impressions). `served` is every
// adResponse row (bids / ad responses, no deviceId gate) and
// `confirmedImpressions` the serves among them whose request fired an
// impression beacon — so confirmed ≤ served and the fill rate is bounded.
// `totalReceivers` stays deviceId-gated (it is a receivers figure).
// Popunder-only figures for the campaign list (plan §10.2). Lifetime, from the
// popEvent ledger: `reserved` is the budget held by unbilled wins (< 24 h),
// `billed` the partner-billed pops, `billable` the pops we charge for (admitted
// visit + confirmation), `gapPct` = the share of pops billed over 24 h ago
// that are not billable (null until one has matured).
export const CampaignPopStatisticsSchema = z.object({
    // Outstanding partner wins at OUR buy price; admin-only (absent for the
    // campaign's owner, who never sees the buy side).
    reserved: z.number().optional(),
    billed: z.number(),
    visits: z.number(),
    ivtVisits: z.number(),
    billable: z.number(),
    gapPct: z.number().nullable(),
});
export const CampaignStatisticsSchema = z.object({
    totalReceivers: z.number(),
    uniqueReceivers: z.number(),
    served: z.number(),
    spent: z.number(),
    // What the owner is charged (sell side, billable basis). budgetCap is
    // enforced against this, so the budget bar reads it. For a non-admin owner
    // `spent` is the same figure.
    charged: z.number().optional(),
    confirmedImpressions: z.number(),
    // Serves whose request fired the player start beacon (per-serve, request-
    // level, like confirmedImpressions; usually ≤ it, but a player may drop the
    // impression beacon and still report start, so readers clamp). VTR on the
    // dashboard = views ÷ confirmedImpressions. Always 0 for popunder.
    views: z.number(),
    pop: CampaignPopStatisticsSchema.optional(),
});
export const CampaignAnalyticsQuerySchema = z.object({
    days: z.string().optional().default("30"),
});
//# sourceMappingURL=campaign.js.map